import { CustomerListAndMerchantListApi } from 'services/Api/CustomerListAndMerchantList'
import { API_RESPONSE_TYPE, ENTITY_TYPE } from '../../constants'

/**
 * An account row for the consolidated report, normalized from the list API.
 * Balances are in rupees (signed: negative = due). `deleted` accounts are still
 * included so their historical transactions appear (Android parity).
 */
export interface IAccountReportAccountFull {
  id: string
  name: string
  balance: number
  mobile?: string
  /** `last_activity` (customer) / `last_activity_time` (supplier), epoch seconds. */
  lastActivity: number
  deleted: boolean
}

const extractList = (response: any, isCustomer: boolean): any[] => {
  if (response?.type !== API_RESPONSE_TYPE.SUCCESS) {
    return []
  }
  const list = isCustomer ? response.data : response.data?.suppliers
  return Array.isArray(list) ? list : []
}

const mapCustomer = (raw: any): IAccountReportAccountFull => ({
  id: raw.id,
  name: raw.description || '',
  balance: Number(raw.balance) || 0, // customer list balance is already in rupees
  mobile: raw.mobile,
  lastActivity: Number(raw.last_activity) || 0,
  deleted: !!raw.deleted
})

const mapSupplier = (raw: any): IAccountReportAccountFull => ({
  id: raw.id,
  name: raw.name || '',
  balance: (Number(raw.balance) || 0) / 100, // supplier list balance is in paise
  mobile: raw.mobile,
  lastActivity: Number(raw.last_activity_time) || 0,
  deleted: !!raw.deleted
})

/**
 * Fetches both the active and deleted account lists for an entity type and merges
 * them into a single normalized list (deduped by id, active winning on conflict).
 */
export const fetchAccountReportAccounts = async (
  entityType: ENTITY_TYPE
): Promise<IAccountReportAccountFull[]> => {
  const isCustomer = entityType === ENTITY_TYPE.CUSTOMER

  const [activeRes, deletedRes] = await Promise.all([
    isCustomer
      ? CustomerListAndMerchantListApi.getCustomersList()
      : CustomerListAndMerchantListApi.getSuppliersList(),
    isCustomer
      ? CustomerListAndMerchantListApi.getDeletedCustomersList()
      : CustomerListAndMerchantListApi.getDeletedSuppliersList()
  ])

  const map = isCustomer ? mapCustomer : mapSupplier
  const activeRaw = extractList(activeRes, isCustomer)
  const deletedRaw = extractList(deletedRes, isCustomer)
  const active = activeRaw
    .filter((raw) => raw && raw.id)
    .map((raw) => ({ ...map(raw), deleted: false }))
  const deleted = deletedRaw
    .filter((raw) => raw && raw.id)
    .map((raw) => ({ ...map(raw), deleted: true }))

  const byId = new Map<string, IAccountReportAccountFull>()
  // Deleted first, then active overrides if an id somehow appears in both.
  for (const account of deleted) {
    byId.set(account.id, account)
  }
  for (const account of active) {
    byId.set(account.id, account)
  }
  return Array.from(byId.values())
}
